/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useParams } from 'js/lib/useRouter';

import { CardMetadata, ProductCard, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import type { PrivateCourseFieldsFragment } from 'bundles/enterprise-collections/queries/__generated__/PrivateCourses';
import { getStatusTagRedesignExperimentVariant } from 'bundles/product-card/components/utils/getStatusTagRedesignExperimentVariant';

import _t from 'i18n!nls/consumer-home';

type Props = {
  customCourse: PrivateCourseFieldsFragment;
  isDesktopListView?: boolean;
  itemIndex: number;
};

const styles = {
  targetCardWrapper: css`
    height: 100%;
  `,

  targetCardWrapperCtrl: css`
    .cds-ProductCard-list {
      border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    }
  `,

  listCardContainer: css`
    /* intentionally unset min-height for list card that was default from CDS */
    min-height: unset;

    .cds-ProductCard-list {
      height: inherit;

      /* intentionally unset min-width for list card that was default from CDS */
      min-width: unset;
    }
  `,
};

const EnterpriseCustomCourseProductCard = ({ customCourse, isDesktopListView, itemIndex }: Props) => {
  const track = useTracker();
  const { programSlug } = useParams();
  const isTabletOrSmaller = useMediaQuery(breakpoints.down('sm'));
  const shouldShowListView = isDesktopListView || isTabletOrSmaller;

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_product_card',
    {
      productCard: {
        index: itemIndex,
      },
      product: {
        id: customCourse.id,
        slug: customCourse?.slug,
        type: 'custom_course',
      },
      enterprise: {
        ...(programSlug ? { programSlug } : {}),
      },
    }
  );

  const { isBorderlessCard, productCardExperimentVariant } = getStatusTagRedesignExperimentVariant();
  const variantType = !shouldShowListView ? 'grid' : 'list';

  return (
    <div
      css={[
        styles.targetCardWrapper,
        !isBorderlessCard && styles.targetCardWrapperCtrl,
        variantType === 'list' ? styles.listCardContainer : null,
      ]}
    >
      <ProductCard
        experimentVariant={productCardExperimentVariant}
        ref={visibilityTrackingRef}
        data-testid="custom-course"
        productType="Course"
        variant={variantType}
        title={{
          name: customCourse.name,
          linkProps: {
            href: `/programs/${programSlug}/learn/${customCourse.slug}`,
          },
        }}
        partners={customCourse.partners.map((partner) => ({
          name: partner.name,
          logoUrl: partner.squareLogo || undefined,
        }))}
        previewImageSrc={customCourse.promoPhotoUrl ?? undefined}
        // eslint-disable-next-line no-restricted-syntax
        footer={<CardMetadata metadata={[_t('Custom course')]} />}
        onClick={() => {
          track('click_product_card', {
            productCard: {
              index: itemIndex,
            },
            product: {
              id: customCourse?.id,
              slug: customCourse?.slug,
              type: 'custom_course',
            },
            enterprise: {
              ...(programSlug ? { programSlug } : {}),
            },
          });
        }}
      />
    </div>
  );
};

export default EnterpriseCustomCourseProductCard;
